<template>
    <div v-if="isOpen" class="fixed inset-0 z-[70]" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0">
                <div class="h-full w-full bg-white">
                    <!-- 헤더 -->
                    <div class="fixed top-0 left-0 right-0 z-10 bg-white border-b border-gray-200">
                        <div class="max-w-5xl mx-auto">
                            <div class="flex items-center justify-between px-4 py-2 sm:py-3">
                                <div class="flex items-center gap-2 sm:gap-3 min-w-0">
                                    <button @click="$emit('close')"
                                        class="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100">
                                        <XIcon class="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                    <h2 class="text-sm sm:text-lg font-semibold text-gray-900">이력서</h2>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 메인 컨텐츠 -->
                    <div class="pt-[60px] h-screen overflow-y-auto">
                        <div class="max-w-4xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
                            <!-- 기본 정보 -->
                            <div class="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                                <div class="flex flex-col sm:flex-row gap-4 sm:gap-8">
                                    <div
                                        class="w-28 h-36 sm:w-40 sm:h-48 mx-auto sm:mx-0 flex-shrink-0 border border-gray-200 rounded-lg overflow-hidden">
                                        <div v-if="!resume.photo"
                                            class="w-full h-full flex items-center justify-center bg-gray-50 cursor-pointer"
                                            @click="localEditMode && $refs.profileInput.click()">
                                            <UserIcon class="w-7 h-7 sm:w-12 sm:h-12 text-gray-300" />
                                        </div>
                                        <div v-else class="w-full h-full relative group cursor-pointer"
                                            @click="localEditMode && $refs.profileInput.click()">
                                            <img :src="resume.photo" alt="프로필 사진" class="w-full h-full object-cover" />
                                            <div v-if="localEditMode"
                                                class="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                <span class="text-white text-xs sm:text-sm">이미지 변경</span>
                                            </div>
                                        </div>
                                        <input type="file" ref="profileInput" class="hidden" accept="image/*"
                                            @change="handleProfileImageChange" />
                                    </div>
                                    <div class="flex-1">
                                        <div class="grid grid-cols-2 gap-2 sm:gap-x-8 sm:gap-y-4">
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">이름</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ resume.name }}</div>
                                                <input v-else v-model="resume.name" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="이름을 입력하세요" />
                                            </div>
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">성별</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ resume.gender === '0' ? '남성' : resume.gender === '1' ? '여성' : ''
                                                    }}
                                                </div>
                                                <select v-else v-model="resume.gender"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm">
                                                    <option value="0">남성</option>
                                                    <option value="1">여성</option>
                                                </select>
                                            </div>
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">생년월일</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm flex items-center">
                                                    <CalendarIcon
                                                        class="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-1 sm:mr-2" />
                                                    {{ formatBirthDate(resume.birthDate) }}
                                                </div>
                                                <div v-else class="relative">
                                                    <div class="relative">
                                                        <input v-model="resume.birthDate" type="text" maxlength="10"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateDate(resume.birthDate) && resume.birthDate }"
                                                            placeholder="YYYY.MM.DD" @input="validateBirthDate" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                    <div v-if="!validateDate(resume.birthDate) && resume.birthDate"
                                                        class="text-xs text-red-500 mt-1 block">올바른 생년월일 형식이 아닙니다
                                                        (YYYY.MM.DD)</div>
                                                </div>
                                            </div>
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">연락처</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ formatPhoneNumber(resume.phone) }}</div>
                                                <input v-else v-model="resume.phone" type="tel"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="010-0000-0000" @input="validatePhoneNumber" />
                                            </div>
                                            <div class="w-full col-span-2" v-if="localEditMode || resume.address">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">주소</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ resume.address }}</div>
                                                <input v-else v-model="resume.address" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="주소를 입력하세요" />
                                            </div>
                                            <div class="w-full col-span-2" v-if="localEditMode || resume.email">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">이메일</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ resume.email }}</div>
                                                <input v-else v-model="resume.email" type="email"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="example@email.com" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 학력 사항 -->
                            <div v-if="localEditMode || resume.education.length > 0"
                                :class="['mb-4 sm:mb-6', { 'pb-4 sm:pb-6 border-b border-gray-200': hasNextSection('education') }]">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">학력 사항</h3>
                                        <span class="ml-1 sm:ml-2 text-xs text-gray-500">({{
                                            resume.education.length }})</span>
                                    </div>
                                    <button v-if="localEditMode" @click="addEducation"
                                        class="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center">
                                        <PlusIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" />
                                        추가
                                    </button>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div v-for="(edu, index) in resume.education" :key="index"
                                        class="border border-gray-200 rounded-lg p-2 sm:p-4 max-w-[320px] w-full">
                                        <div class="grid grid-cols-1 gap-2">
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">학교명</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ edu.school }}</div>
                                                <input v-else v-model="edu.school" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="학교명을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">전공</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ edu.major }}</div>
                                                <input v-else v-model="edu.major" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="전공을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="flex items-center justify-between mb-1">
                                                    <div class="text-xs sm:text-sm text-gray-600">재학기간</div>
                                                    <label v-if="localEditMode"
                                                        class="flex items-center gap-1 text-xs sm:text-sm text-gray-600 cursor-pointer">
                                                        <input type="checkbox" v-model="edu.isStudying"
                                                            @change="handleStudyingChange(edu)"
                                                            class="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                                        재학중
                                                    </label>
                                                </div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm flex items-center">
                                                    <CalendarIcon
                                                        class="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-1 sm:mr-2" />
                                                    {{ formatDate(edu.startDate) }} ~ {{ edu.endDate === '재학중' ? '재학중' :
                                                        formatDate(edu.endDate) }}
                                                </div>
                                                <div v-else class="flex items-center gap-1">
                                                    <div class="relative flex-1">
                                                        <input v-model="edu.startDate" type="text"
                                                            @input="validateEduStartDate($event, edu)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(edu.startDate).isValid && edu.startDate }"
                                                            placeholder="YYYY.MM" maxlength="7" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                    <span class="text-gray-400">~</span>
                                                    <div class="relative flex-1">
                                                        <input v-model="edu.endDate" type="text"
                                                            @input="validateEduEndDate($event, edu)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(edu.endDate).isValid && edu.endDate && edu.endDate !== '재학중' }"
                                                            placeholder="YYYY.MM" maxlength="7"
                                                            :disabled="edu.isStudying" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                </div>
                                                <div v-if="!validateYearMonth(edu.startDate).isValid && edu.startDate"
                                                    class="text-xs text-red-500 mt-1 block">올바른 입학일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                                <div v-if="!validateYearMonth(edu.endDate).isValid && edu.endDate && edu.endDate !== '재학중'"
                                                    class="text-xs text-red-500 mt-1 block">올바른 졸업일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                            </div>
                                        </div>
                                        <button v-if="localEditMode" @click="removeEducation(index)"
                                            class="mt-2 sm:mt-4 text-xs sm:text-sm text-red-600 hover:text-red-700 flex items-center">
                                            <TrashIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- 경력 사항 -->
                            <div v-if="localEditMode || resume.careers.length > 0"
                                :class="['mb-4 sm:mb-6', { 'pb-4 sm:pb-6 border-b border-gray-200': hasNextSection('careers') }]">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">경력 사항</h3>
                                        <span class="ml-1 sm:ml-2 text-xs text-gray-500">({{
                                            resume.careers.length }})</span>
                                    </div>
                                    <button v-if="localEditMode" @click="addCareer"
                                        class="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center">
                                        <PlusIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" />
                                        추가
                                    </button>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div v-for="(career, index) in resume.careers" :key="index"
                                        class="border border-gray-200 rounded-lg p-2 sm:p-4 max-w-[320px] w-full">
                                        <div class="grid grid-cols-1 gap-2">
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">회사명</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ career.company }}</div>
                                                <input v-else v-model="career.company" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="회사명을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">직위</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ career.position }}</div>
                                                <input v-else v-model="career.position" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="직위를 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="flex items-center justify-between mb-1">
                                                    <div class="text-xs sm:text-sm text-gray-600">재직기간</div>
                                                    <label v-if="localEditMode"
                                                        class="flex items-center gap-1 text-xs sm:text-sm text-gray-600 cursor-pointer">
                                                        <input type="checkbox" v-model="career.isWorking"
                                                            @change="handleWorkingChange(career)"
                                                            class="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                                        재직중
                                                    </label>
                                                </div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm flex items-center">
                                                    <CalendarIcon
                                                        class="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-1 sm:mr-2" />
                                                    {{ formatDate(career.startDate) }} ~ {{ career.endDate === '재직중' ?
                                                        '재직중' : formatDate(career.endDate) }}
                                                </div>
                                                <div v-else class="flex items-center gap-1">
                                                    <div class="relative flex-1">
                                                        <input v-model="career.startDate" type="text"
                                                            @input="validateCareerStartDate($event, career)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(career.startDate).isValid && career.startDate }"
                                                            placeholder="YYYY.MM" maxlength="7" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                    <span class="text-gray-400">~</span>
                                                    <div class="relative flex-1">
                                                        <input v-model="career.endDate" type="text"
                                                            @input="validateCareerEndDate($event, career)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(career.endDate).isValid && career.endDate && career.endDate !== '재직중' }"
                                                            placeholder="YYYY.MM" maxlength="7"
                                                            :disabled="career.isWorking" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                </div>
                                                <div v-if="!validateYearMonth(career.startDate).isValid && career.startDate"
                                                    class="text-xs text-red-500 mt-1 block">올바른 입사일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                                <div v-if="!validateYearMonth(career.endDate).isValid && career.endDate && career.endDate !== '재직중'"
                                                    class="text-xs text-red-500 mt-1 block">올바른 퇴사일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                            </div>
                                        </div>
                                        <button v-if="localEditMode" @click="removeCareer(index)"
                                            class="mt-2 sm:mt-4 text-xs sm:text-sm text-red-600 hover:text-red-700 flex items-center">
                                            <TrashIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- 자격 사항 -->
                            <div v-if="localEditMode || resume.certifications.length > 0"
                                :class="['mb-4 sm:mb-6', { 'pb-4 sm:pb-6 border-b border-gray-200': hasNextSection('certifications') }]">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">자격 사항</h3>
                                        <span class="ml-1 sm:ml-2 text-xs text-gray-500">({{
                                            resume.certifications.length }})</span>
                                    </div>
                                    <button v-if="localEditMode" @click="addCertification"
                                        class="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center">
                                        <PlusIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" />
                                        추가
                                    </button>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div v-for="(cert, index) in resume.certifications" :key="index"
                                        class="border border-gray-200 rounded-lg p-2 sm:p-4 max-w-[320px] w-full">
                                        <div class="grid grid-cols-1 gap-2">
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">자격증명</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ cert.name }}</div>
                                                <input v-else v-model="cert.name" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="자격증명을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">발급기관</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm">
                                                    {{ cert.organization }}</div>
                                                <input v-else v-model="cert.organization" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="발급기관을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">취득일</div>
                                                <div v-if="!localEditMode"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg min-h-[28px] sm:min-h-[36px] bg-gray-50 text-xs sm:text-sm flex items-center">
                                                    <CalendarIcon
                                                        class="w-3 h-3 sm:w-4 sm:h-4 text-gray-400 mr-1 sm:mr-2" />
                                                    {{ formatDate(cert.date) }}
                                                </div>
                                                <div v-else class="relative">
                                                    <div class="relative">
                                                        <input v-model="cert.date" type="text"
                                                            @input="validateCertDate($event, cert)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(cert.date).isValid && cert.date }"
                                                            placeholder="YYYY 또는 YYYY.MM 또는 YYYY.MM.DD"
                                                            maxlength="10" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                    <div v-if="!validateYearMonth(cert.date).isValid && cert.date"
                                                        class="text-xs text-red-500 mt-1 block">올바른 취득일 형식이 아닙니다
                                                        (YYYY 또는 YYYY.MM 또는 YYYY.MM.DD)</div>
                                                </div>
                                            </div>
                                        </div>
                                        <button v-if="localEditMode" @click="removeCertification(index)"
                                            class="mt-2 sm:mt-4 text-xs sm:text-sm text-red-600 hover:text-red-700 flex items-center">
                                            <TrashIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- 포트폴리오 및 문서 파일 -->
                            <div v-if="localEditMode || (resume.documents && resume.documents.length > 0)"
                                :class="['mb-4 sm:mb-6', { 'pb-4 sm:pb-6 border-b border-gray-200': hasNextSection('documents') }]">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">포트폴리오 및 문서 파일</h3>
                                        <span class="ml-2 text-xs text-gray-500">({{ documentCount }})</span>
                                    </div>
                                </div>
                                <div class="bg-white p-3 sm:p-4 rounded-md border border-gray-200">
                                    <div v-if="localEditMode" class="mb-4">
                                        <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-2">
                                            <label class="text-sm font-medium text-gray-600">첨부 파일</label>
                                            <div class="text-xs text-gray-500 mt-1 sm:mt-0">최대 10MB, 5개까지 첨부 가능</div>
                                        </div>

                                        <div class="border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                                            @click="$refs.documentInput.click()" @dragover.prevent
                                            @drop.prevent="handleFileDrop" @dragleave.prevent>
                                            <FileIcon class="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-gray-400" />
                                            <p class="text-sm text-gray-500 mb-1">파일을 드래그하거나 클릭하여 업로드</p>
                                            <p class="text-xs text-gray-400">포트폴리오, 이력서, 추천서 등</p>
                                        </div>

                                        <input type="file" ref="documentInput" class="hidden"
                                            @change="handleDocumentUpload" multiple
                                            accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.jpg,.jpeg,.png,.hwp,.xlsx" />
                                    </div>

                                    <!-- 파일 목록 -->
                                    <div v-if="showFileList" class="">
                                        <h3 class="text-sm font-medium text-gray-600 mb-2">첨부 목록 ({{ documentCount }})
                                        </h3>
                                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div v-for="(doc, index) in resume.tempDocuments" :key="index"
                                                class="flex items-center justify-between p-2 sm:p-3 border border-gray-200 rounded-md bg-gray-50">
                                                <div class="flex items-center min-w-0">
                                                    <div class="mr-3 flex-shrink-0">
                                                        <FileTextIcon v-if="doc.type?.includes('pdf')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                                                        <FileIcon
                                                            v-else-if="doc.type?.includes('word') || doc.type?.includes('doc')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                                                        <FileIcon
                                                            v-else-if="doc.type?.includes('presentation') || doc.type?.includes('ppt')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                                                        <FileArchiveIcon
                                                            v-else-if="doc.type?.includes('zip') || doc.type?.includes('archive')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                                                        <ImageIcon
                                                            v-else-if="doc.type?.includes('image') || doc.type?.includes('jpg') || doc.type?.includes('jpeg') || doc.type?.includes('png')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-sky-500" />
                                                        <FileIcon v-else-if="doc.type?.includes('hwp')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                                                        <FileIcon
                                                            v-else-if="doc.type?.includes('sheet') || doc.type?.includes('xlsx')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                                                        <FileIcon v-else class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                                    </div>
                                                    <div class="flex-1 min-w-0">
                                                        <p
                                                            class="text-xs sm:text-sm font-medium text-gray-700 truncate">
                                                            {{
                                                                doc.name }}</p>
                                                        <p v-if="doc.is_new" class="text-xs text-gray-500">{{
                                                            formatFileSize(doc.size) }}</p>
                                                    </div>
                                                </div>
                                                <div class="flex items-center space-x-2 ml-4">
                                                    <button v-if="localEditMode" @click="removeDocument(index)"
                                                        class="text-red-500 hover:text-red-700 transition-colors p-1"
                                                        title="삭제">
                                                        <TrashIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                    </button>
                                                    <button v-else @click="downloadFile(doc)"
                                                        class="text-blue-500 hover:text-blue-700 transition-colors p-1"
                                                        title="다운로드">
                                                        <DownloadIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 자기소개 -->
                            <div v-if="localEditMode || resume.introduction" class="mb-4 sm:mb-6">
                                <h3 class="text-sm sm:text-base font-semibold text-gray-900">자기소개</h3>
                                <div class="p-3 sm:p-4">
                                    <div v-if="!localEditMode"
                                        class="w-full p-2 border border-gray-200 rounded-lg min-h-[80px] sm:min-h-[120px] whitespace-pre-wrap bg-gray-50 text-xs sm:text-sm">
                                        {{ resume.introduction }}</div>
                                    <textarea v-else v-model="resume.introduction"
                                        class="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 h-20 sm:h-28 resize-none text-xs sm:text-sm"
                                        placeholder="자기소개를 입력하세요"></textarea>
                                </div>
                            </div>

                            <!-- 저장 버튼 -->
                            <div v-if="localEditMode" class="flex justify-center">
                                <button @click="saveResume"
                                    class="px-4 sm:px-6 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-xs sm:text-sm">
                                    저장하기
                                </button>
                            </div>

                            <!-- 수정하기 버튼 -->
                            <div v-else class="flex justify-center mt-4">
                                <button @click="startEditMode"
                                    class="px-4 sm:px-6 py-1.5 sm:py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium text-xs sm:text-sm">
                                    수정하기
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { XIcon, UserIcon, PlusIcon, TrashIcon, FileIcon, FileTextIcon, FileArchiveIcon, ImageIcon, CalendarIcon, DownloadIcon } from 'lucide-vue-next'
import Swal from 'sweetalert2'
import resumeAPI from '@/api/resume/resume'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true
    },
    resumeData: {
        type: Object,
        required: true
    },
    isEditMode: {
        type: Boolean,
        default: false
    }
})

const default_url = process.env.VUE_APP_BEFORE_API_URL

const resume = ref({
    photo: '',
    name: '',
    gender: '',
    birthDate: '',
    phone: '',
    address: '',
    email: '',
    education: [],
    careers: [],
    certifications: [],
    documents: [],
    tempDocuments: [],
    deletedFiles: [],
    introduction: ''
})

// 파일 입력 참조
const documentInput = ref(null)
const profileInput = ref(null)

// isEditMode 로컬 상태 추가
const localEditMode = ref(props.isEditMode)

// isEditMode prop 변경 감지
watch(() => props.isEditMode, (newValue) => {
    localEditMode.value = newValue
})

// 로컬 상태 변경 시 부모 컴포넌트에 알림
watch(localEditMode, (newValue) => {
    emit('update:isEditMode', newValue)
})

// 유틸리티 함수들
const formatPhoneNumber = (phone) => {
    if (!phone) return ''
    // 숫자만 추출
    const numbers = phone.replace(/[^0-9]/g, '')
    if (numbers.length >= 10) {
        return numbers.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
    }
    return phone
}

const formatDate = (date) => {
    if (!date) return ''
    // ISO 날짜 문자열인 경우 처리
    if (date.includes('T')) {
        const d = new Date(date)
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
    }
    // YYYY-MM-DD 형식인 경우 처리
    if (date.includes('-')) {
        return date.split('-').join('.')
    }
    return date
}

const formatBirthDate = (date) => {
    if (!date) return ''
    // ISO 날짜 문자열인 경우 처리
    if (date.includes('T')) {
        const d = new Date(date)
        return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
    }
    // YYYY-MM-DD 형식인 경우 처리
    if (date.includes('-')) {
        return date.split('-').join('.')
    }
    return date
}

const validateBirthDate = (event) => {
    let value = event.target.value
    // 숫자만 추출
    value = value.replace(/[^0-9]/g, '')
    
    // 숫자 길이에 따라 점 추가
    if (value.length > 4) {
        value = value.slice(0, 4) + '.' + value.slice(4)
        if (value.length > 7) {
            value = value.slice(0, 7) + '.' + value.slice(7)
        }
    }
    
    // 최대 길이 제한 (YYYY.MM.DD)
    if (value.length > 10) {
        value = value.slice(0, 10)
    }
    
    resume.value.birthDate = value
}

// 날짜 유효성 검사 함수 개선
const validateDate = (dateString) => {
    if (!dateString) return true;
    
    // YYYY.MM.DD 형식 검사
    const pattern = /^\d{4}\.\d{2}\.\d{2}$/;
    if (!pattern.test(dateString)) return false;
    
    const [year, month, day] = dateString.split('.').map(Number);
    const date = new Date(year, month - 1, day);
    
    // 유효한 날짜인지 확인
    return date.getFullYear() === year &&
           date.getMonth() === month - 1 &&
           date.getDate() === day &&
           year >= 1900 &&
           year <= new Date().getFullYear();
}

// YYYY.MM 형식의 날짜 유효성 검사 함수 개선
const validateYearMonth = (value) => {
    if (!value) return { value: '', isValid: true };
    
    // 숫자만 추출
    const numericValue = value.replace(/[^0-9]/g, '')
    let formattedValue = ''
    
    // 숫자 길이에 따라 점 추가
    if (numericValue.length > 4) {
        formattedValue = numericValue.slice(0, 4) + '.' + numericValue.slice(4, 6)
    } else {
        formattedValue = numericValue
    }
    
    // 최대 길이 제한 (YYYY.MM)
    if (formattedValue.length > 7) {
        formattedValue = formattedValue.slice(0, 7)
    }

    // 날짜 유효성 검사
    let isValid = true
    if (formattedValue.length === 7) {
        const pattern = /^\d{4}\.\d{2}$/;
        if (!pattern.test(formattedValue)) {
            isValid = false;
        } else {
            const year = parseInt(formattedValue.slice(0, 4))
            const month = parseInt(formattedValue.slice(5, 7))
            isValid = year >= 1900 && 
                     year <= new Date().getFullYear() && 
                     month >= 1 && 
                     month <= 12;
        }
    }
    
    return {
        value: formattedValue,
        isValid
    }
}

// 각 날짜 입력 필드의 유효성 검사 함수
const validateEduStartDate = (event, edu) => {
    const result = validateYearMonth(event.target.value)
    edu.startDate = result.value
    edu.startDateError = !result.isValid
}

const validateEduEndDate = (event, edu) => {
    const result = validateYearMonth(event.target.value)
    edu.endDate = result.value
    edu.endDateError = !result.isValid
}

const validateCareerStartDate = (event, career) => {
    const result = validateYearMonth(event.target.value)
    career.startDate = result.value
    career.startDateError = !result.isValid
}

const validateCareerEndDate = (event, career) => {
    const result = validateYearMonth(event.target.value)
    career.endDate = result.value
    career.endDateError = !result.isValid
}

const validateCertDate = (event, cert) => {
    let value = event.target.value
    // 숫자와 점만 허용
    value = value.replace(/[^0-9.]/g, '')
    
    // 연도만 입력된 경우 (YYYY)
    if (value.length <= 4) {
        cert.date = value
        return
    }
    
    // 연도와 월이 입력된 경우 (YYYY.MM)
    if (value.length <= 7) {
        if (value.length === 5 && !value.includes('.')) {
            value = value.slice(0, 4) + '.' + value.slice(4)
        }
        cert.date = value
        return
    }
    
    // 연도, 월, 일이 입력된 경우 (YYYY.MM.DD)
    if (value.length <= 10) {
        if (value.length === 5 && !value.includes('.')) {
            value = value.slice(0, 4) + '.' + value.slice(4)
        }
        if (value.length === 8 && value.split('.').length === 2) {
            value = value.slice(0, 7) + '.' + value.slice(7)
        }
        cert.date = value
    }
}

const validatePhoneNumber = (event) => {
    let value = event.target.value
    // 숫자만 추출
    const numbers = value.replace(/[^0-9]/g, '')
    
    // 숫자 길이에 따라 하이픈 추가
    if (numbers.length <= 3) {
        value = numbers
    } else if (numbers.length <= 7) {
        value = numbers.slice(0, 3) + '-' + numbers.slice(3)
    } else {
        value = numbers.slice(0, 3) + '-' + numbers.slice(3, 7) + '-' + numbers.slice(7, 11)
    }
    
    resume.value.phone = value
}

// 파일 크기 포맷팅
const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 파일 확장자 추출 유틸리티 함수
const getFileExtension = (filename) => {
    if (!filename) return ''
    const lastDotIndex = filename.lastIndexOf('.')
    return lastDotIndex === -1 ? '' : filename.slice(lastDotIndex + 1).toLowerCase()
}

// 문서 파일 업로드 처리
const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return

    const newFiles = Array.from(files)
    const maxSize = 10 * 1024 * 1024 // 10MB
    let hasInvalidFile = false

    for (const file of newFiles) {
        const fileExtension = getFileExtension(file.name)
        const allowedExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'zip', 'jpg', 'jpeg', 'png', 'hwp', 'xlsx']

        if (!allowedExtensions.includes(fileExtension)) {
            await Swal.fire({
                icon: 'error',
                title: '파일 형식 오류',
                text: `${file.name} 파일은 지원하지 않는 형식입니다.`
            })
            hasInvalidFile = true
            break
        }

        if (file.size > maxSize) {
            await Swal.fire({
                icon: 'error',
                title: '파일 크기 초과',
                text: `${file.name} 파일이 10MB를 초과합니다.`
            })
            hasInvalidFile = true
            break
        }
    }

    if (hasInvalidFile) return

    const totalFiles = (resume.value.tempDocuments?.length || 0) + newFiles.length
    if (totalFiles > 5) {
        await Swal.fire({
            icon: 'warning',
            title: '파일 개수 초과',
            text: '최대 5개의 파일만 업로드할 수 있습니다.'
        })
        return
    }

    for (const file of newFiles) {
        const fileType = file.type || `application/${getFileExtension(file.name)}`
        const newDoc = {
            name: file.name,
            type: fileType,
            size: file.size,
            file: file,
            file_name: file.name,
            original_name: file.name
        }

        if (!resume.value.tempDocuments) {
            resume.value.tempDocuments = []
        }
        resume.value.tempDocuments.push(newDoc)
    }
}

// 파일 드래그 앤 드롭 이벤트 핸들러
const handleFileDrop = (event) => {
    event.preventDefault()
    const files = event.dataTransfer.files
    if (files.length > 0) {
        handleFileUpload(files)
    }
}

// 파일 선택 이벤트 핸들러
const handleDocumentUpload = (event) => {
    const files = event.target.files
    if (files.length > 0) {
        handleFileUpload(files)
    }
    // 파일 선택 초기화
    event.target.value = ''
}

// 파일 목록 표시 영역 수정
const showFileList = computed(() => {
    return localEditMode.value || (resume.value.tempDocuments && resume.value.tempDocuments.length > 0)
})

// 파일 개수 표시
const documentCount = computed(() => {
    return resume.value.tempDocuments?.length || 0
})

// 임시 파일 삭제
const removeDocument = (index) => {
    const file = resume.value.tempDocuments[index]
    // 기존 파일인 경우 deletedFiles에 추가
    if (file.id) {
        if (!resume.value.deletedFiles) {
            resume.value.deletedFiles = []
        }
        resume.value.deletedFiles.push({
            id: file.id,
            file_name: file.file_name,
            original_name: file.original_name,
            is_new: file.is_new,
            upload_year: file.upload_year
        })
    }
    // tempDocuments에서 제거
    resume.value.tempDocuments.splice(index, 1)
}

// 부모 컴포넌트에서 전달받은 데이터로 초기화
const parsePeriod = (period, type) => {
    if (!period) return { startDate: '', endDate: '' }
    const [startDate, endDate] = period.split('~').map((date, index) => {
        const trimmedDate = date.trim()
        if (!trimmedDate) return ''
        // 종료일이 9999인 경우 type에 따라 '재학중' 또는 '재직중' 표시
        if (index === 1 && trimmedDate === '9999') {
            return type === 'education' ? '재학중' : '재직중'
        }
        // YYYYMM 형식을 YYYY.MM 형식으로 변환
        return `${trimmedDate.slice(0, 4)}.${trimmedDate.slice(4, 6)}`
    })
    return { startDate, endDate }
}

const initializeResume = () => {
    if (props.resumeData) {
        resume.value = {
            photo: props.resumeData.profile_image ? 
                (props.resumeData.is_new ? 
                    `${process.env.VUE_APP_S3_URL}/_static/resume/profile/${props.resumeData.upload_year}/${props.resumeData.profile_image}` :
                    `https://work-api.agabang.com:6300/images/resume/${props.resumeData.profile_image}`) : '',
            name: props.resumeData.name || '',
            gender: props.resumeData.sex?.toString() || '1',
            birthDate: formatBirthDate(props.resumeData.dob || ''),
            phone: formatPhoneNumber(props.resumeData.hp || ''),
            address: props.resumeData.address || '',
            email: props.resumeData.email || '',
            education: props.resumeData.education_history?.map(edu => {
                const { startDate, endDate } = parsePeriod(edu.period, 'education')
                return {
                    school: edu.name,
                    major: edu.note,
                    startDate,
                    endDate,
                    isStudying: endDate === '재학중'
                }
            }) || [],
            careers: props.resumeData.career_history?.map(career => {
                const { startDate, endDate } = parsePeriod(career.period, 'career')
                return {
                    company: career.name,
                    position: career.note,
                    startDate,
                    endDate,
                    isWorking: endDate === '재직중'
                }
            }) || [],
            certifications: props.resumeData.certification_history?.map(cert => {
                let formattedDate = ''
                if (cert.period) {
                    const period = cert.period
                    if (period.length === 4) {
                        formattedDate = period // YYYY
                    } else if (period.length === 6) {
                        formattedDate = `${period.slice(0, 4)}.${period.slice(4, 6)}` // YYYY.MM
                    } else if (period.length === 8) {
                        formattedDate = `${period.slice(0, 4)}.${period.slice(4, 6)}.${period.slice(6, 8)}` // YYYY.MM.DD
                    }
                }
                return {
                    name: cert.name,
                    organization: cert.note,
                    date: formattedDate
                }
            }) || [],
            documents: props.resumeData.attachment?.map(doc => ({
                id: doc.id,
                name: doc.original_name,
                type: doc.file_name ? getFileExtension(doc.file_name) : '',
                size: doc.size || 0,
                file_name: doc.file_name,
                upload_year: doc.upload_year,
                is_new: doc.is_new,
                url: doc.is_new ? 
                    `${process.env.VUE_APP_S3_URL}/_static/resume/file/${doc.upload_year}/${doc.file_name}` : 
                    `${default_url}/api/general/manager_resume/getAttachmentDownload?file_name=${doc.file_name}`
            })) || [],
            tempDocuments: [],
            deletedFiles: [],
            introduction: props.resumeData.note || ''
        }

        // 기존 첨부파일을 tempDocuments로 복사
        if (resume.value.documents && resume.value.documents.length > 0) {
            resume.value.tempDocuments = resume.value.documents.map(doc => ({
                ...doc,
                file: null // 실제 파일 객체는 없으므로 null로 설정
            }))
            console.log('tempDocuments로 복사된 첨부파일:', resume.value.tempDocuments)
        }
    }
}

// Watch for changes in resumeData prop
watch(() => props.resumeData, () => {
    initializeResume()
}, { immediate: true })

// 학력 관련 함수
const addEducation = () => {
    resume.value.education.push({
        school: '',
        major: '',
        startDate: '',
        endDate: '',
        isStudying: false
    })
}

const removeEducation = (index) => {
    resume.value.education.splice(index, 1)
}

// 경력 관련 함수
const addCareer = () => {
    resume.value.careers.push({
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        isWorking: false
    })
}

const removeCareer = (index) => {
    resume.value.careers.splice(index, 1)
}

// 자격증 관련 함수
const addCertification = () => {
    resume.value.certifications.push({
        name: '',
        organization: '',
        date: ''
    })
}

const removeCertification = (index) => {
    resume.value.certifications.splice(index, 1)
}

// 이력서 저장 함수 수정
const saveResume = async () => {
    try {
        // 필수값 검사
        if (!resume.value.name?.trim()) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '이름을 입력해주세요.'
            })
            return
        }

        if (!resume.value.gender) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '성별을 선택해주세요.'
            })
            return
        }

        if (!resume.value.birthDate?.trim()) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '생년월일을 입력해주세요.'
            })
            return
        }

        if (!resume.value.phone?.trim()) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '연락처를 입력해주세요.'
            })
            return
        }

        const formData = new FormData()

        // 기본 정보 변경 확인 및 추가
        if (resume.value.name !== props.resumeData.name) {
            formData.append('name', resume.value.name)
        }
        if (resume.value.gender !== props.resumeData.sex?.toString()) {
            formData.append('sex', resume.value.gender)
        }
        if (resume.value.birthDate.replace(/\./g, '') !== props.resumeData.dob) {
            formData.append('dob', resume.value.birthDate.replace(/\./g, ''))
        }
        if (resume.value.phone.replace(/[^0-9]/g, '') !== props.resumeData.hp) {
            formData.append('hp', resume.value.phone.replace(/[^0-9]/g, ''))
        }
        if (resume.value.address !== props.resumeData.address) {
            formData.append('address', resume.value.address || '')
        }
        if (resume.value.email !== props.resumeData.email) {
            formData.append('email', resume.value.email || '')
        }
        if (resume.value.introduction !== props.resumeData.note) {
            formData.append('note', resume.value.introduction || '')
        }

        // 프로필 이미지 변경 확인
        if (resume.value.profileImageFile) {
            formData.append('profile_image', resume.value.profileImageFile)
            formData.append('profile_thumbnail', resume.value.profileThumbnailFile)
            formData.append('upload_year', resume.value.upload_year || new Date().getFullYear().toString())
            // 기존 프로필 이미지 파일명 추가
            if (props.resumeData.profile_image) {
                const oldFileName = props.resumeData.profile_image.includes('/')
                    ? props.resumeData.profile_image.split('/').pop()
                    : props.resumeData.profile_image
                formData.append('old_profile_image', oldFileName)
            }
        }

        // 학력 정보 변경 확인
        const currentEducation = resume.value.education || []
        const originalEducation = props.resumeData.education_history || []
        if (JSON.stringify(currentEducation) !== JSON.stringify(originalEducation)) {
            formData.append('education_history', JSON.stringify(currentEducation.map(edu => ({
                school: edu.school,
                major: edu.major,
                start_date: edu.startDate.replace(/\./g, ''),
                end_date: edu.isStudying ? '9999' : edu.endDate.replace(/\./g, ''),
                is_studying: edu.isStudying ? 1 : 0
            }))))
        }

        // 경력 정보 변경 확인
        const currentCareers = resume.value.careers || []
        const originalCareers = props.resumeData.career_history || []
        if (JSON.stringify(currentCareers) !== JSON.stringify(originalCareers)) {
            formData.append('career_history', JSON.stringify(currentCareers.map(career => ({
                company: career.company,
                position: career.position,
                start_date: career.startDate.replace(/\./g, ''),
                end_date: career.isWorking ? '9999' : career.endDate.replace(/\./g, ''),
                is_working: career.isWorking ? 1 : 0
            }))))
        }

        // 자격증 정보 변경 확인
        const currentCertifications = resume.value.certifications || []
        const originalCertifications = props.resumeData.certification_history || []
        if (JSON.stringify(currentCertifications) !== JSON.stringify(originalCertifications)) {
            formData.append('certification_history', JSON.stringify(currentCertifications.map(cert => ({
                name: cert.name,
                organization: cert.organization,
                period: cert.date ? cert.date.replace(/\./g, '') : '' // YYYY, YYYYMM, YYYYMMDD 형식으로 변환
            }))))
        }

        // 첨부파일 변경 확인
        const modifiedTypes = []
        
        // 1. 새로 추가된 파일들
        if (resume.value.tempDocuments?.length > 0) {
            resume.value.tempDocuments.forEach(doc => {
                if (doc.file) {  // 새로 추가된 파일인 경우
                    formData.append('files', doc.file)
                    formData.append('upload_year', new Date().getFullYear().toString())
                    // 파일 크기를 KB로 변환하고 반올림
                    const fileSizeInKB = Math.round(doc.file.size / 1024)
                    formData.append('file_size', fileSizeInKB)
                    // 파일명만 저장 (경로 제외)
                    const fileName = doc.file.name.split('/').pop()
                    formData.append('file_name', fileName)
                    formData.append('original_name', doc.original_name)
                    modifiedTypes.push(`add_${fileName}`)
                }
            })
        }

        // 2. 삭제된 파일들 - 필요한 정보를 모두 포함
        if (resume.value.deletedFiles?.length > 0) {
            resume.value.deletedFiles.forEach(file => {
                // 파일명만 추출 (경로 제외)
                const fileName = file.file_name.split('/').pop()
                modifiedTypes.push(JSON.stringify({
                    type: 'delete',
                    id: file.id,
                    file_name: fileName,
                    original_name: file.original_name,
                    is_new: file.is_new,
                    upload_year: file.upload_year,
                    file_size: file.size
                }))
            })
        }

        // 변경사항이 있는 경우에만 modified_types 추가
        if (modifiedTypes.length > 0) {
            formData.append('modified_types', JSON.stringify(modifiedTypes))
        }

        // 변경사항이 없으면 저장하지 않음
        if (formData.entries().next().done) {
            await Swal.fire({
                icon: 'info',
                title: '알림',
                text: '변경된 내용이 없습니다.'
            })
            return
        }

        // 저장 중 로딩 표시
        const loadingToast = Swal.fire({
            title: '저장 중...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading()
            }
        })

        try {
            const response = await resumeAPI.updateResume(props.resumeData.id, formData)
            
            if (response.data.success) {
                await Swal.fire({
                    icon: 'success',
                    title: '성공',
                    text: '이력서가 저장되었습니다.'
                })

                // 수정된 이력서 데이터로 바로 업데이트
                const updatedResume = await resumeAPI.getResumeDetail(props.resumeData.id)
                if (updatedResume.data) {
                    // 현재 컴포넌트의 resume 데이터만 업데이트
                    resume.value = {
                        ...resume.value,
                        name: updatedResume.data.name || '',
                        gender: updatedResume.data.sex?.toString() || '',
                        birthDate: formatBirthDate(updatedResume.data.dob || ''),
                        phone: formatPhoneNumber(updatedResume.data.hp || ''),
                        address: updatedResume.data.address || '',
                        email: updatedResume.data.email || '',
                        education: updatedResume.data.education_history?.map(edu => {
                            const { startDate, endDate } = parsePeriod(edu.period, 'education')
                            return {
                                school: edu.name,
                                major: edu.note,
                                startDate,
                                endDate,
                                isStudying: endDate === '재학중'
                            }
                        }) || [],
                        careers: updatedResume.data.career_history?.map(career => {
                            const { startDate, endDate } = parsePeriod(career.period, 'career')
                            return {
                                company: career.name,
                                position: career.note,
                                startDate,
                                endDate,
                                isWorking: endDate === '재직중'
                            }
                        }) || [],
                        certifications: updatedResume.data.certification_history?.map(cert => {
                            let formattedDate = ''
                            if (cert.period) {
                                const period = cert.period
                                if (period.length === 4) {
                                    formattedDate = period // YYYY
                                } else if (period.length === 6) {
                                    formattedDate = `${period.slice(0, 4)}.${period.slice(4, 6)}` // YYYY.MM
                                } else if (period.length === 8) {
                                    formattedDate = `${period.slice(0, 4)}.${period.slice(4, 6)}.${period.slice(6, 8)}` // YYYY.MM.DD
                                }
                            }
                            return {
                                name: cert.name,
                                organization: cert.note,
                                date: formattedDate
                            }
                        }) || [],
                        documents: updatedResume.data.attachment?.map(doc => ({
                            id: doc.id,
                            name: doc.original_name,
                            type: doc.file_name ? getFileExtension(doc.file_name) : '',
                            size: doc.size || 0,
                            file_name: doc.file_name,
                            upload_year: doc.upload_year,
                            is_new: doc.is_new,
                            url: doc.is_new ? 
                                `${process.env.VUE_APP_S3_URL}/_static/resume/file/${doc.upload_year}/${doc.file_name}` : 
                                `${default_url}/api/general/manager_resume/getAttachmentDownload?file_name=${doc.file_name}`
                        })) || [],
                        introduction: updatedResume.data.note || ''
                    }

                    // tempDocuments 업데이트
                    if (resume.value.documents && resume.value.documents.length > 0) {
                        resume.value.tempDocuments = resume.value.documents.map(doc => ({
                            ...doc,
                            file: null
                        }))
                    }
                    
                    // 조회 모드로 전환
                    localEditMode.value = false
                    emit('update:isEditMode', false)
                }
            }
        } catch (error) {
            console.error('이력서 저장 중 오류:', error)
            await Swal.fire({
                icon: 'error',
                title: '오류',
                text: '이력서 저장 중 오류가 발생했습니다.'
            })
        } finally {
            // 로딩 표시 종료
            await loadingToast.close()
        }
    } catch (error) {
        console.error('이력서 저장 중 오류:', error)
        await Swal.fire({
            icon: 'error',
            title: '오류',
            text: '이력서 저장 중 오류가 발생했습니다.'
        })
    }
}

// 다음 섹션이 있는지 확인하는 함수
const hasNextSection = (currentSection) => {
    const sections = ['education', 'careers', 'certifications', 'documents', 'introduction']
    const currentIndex = sections.indexOf(currentSection)
    
    // 현재 섹션 이후의 섹션들을 확인
    for (let i = currentIndex + 1; i < sections.length; i++) {
        const section = sections[i]
        if (section === 'documents') {
            if (localEditMode.value || (resume.value.documents && resume.value.documents.length > 0)) return true
        } else if (section === 'introduction') {
            if (localEditMode.value || resume.value.introduction) return true
        } else {
            if (localEditMode.value || resume.value[section].length > 0) return true
        }
    }
    return false
}

const handleStudyingChange = (edu) => {
    if (edu.isStudying) {
        edu.endDate = '재학중'
    } else {
        edu.endDate = ''
    }
}

const handleWorkingChange = (career) => {
    if (career.isWorking) {
        career.endDate = '재직중'
    } else {
        career.endDate = ''
    }
}

// 파일 다운로드 함수
const downloadFile = async (doc) => {
    if (!doc?.file_name) return;
    
    try {
        if (doc.is_new) {
            // S3 파일인 경우 백엔드 API를 통해 다운로드
            const response = await resumeAPI.downloadS3File(doc.file_name, doc.upload_year);
            // Content-Type 확인 및 기본값 설정
            let contentType = response.headers?.['content-type'];
            if (!contentType || contentType === 'application/octet-stream') {
                const ext = doc.name.split('.').pop().toLowerCase();
                const mimeTypes = {
                    'pdf': 'application/pdf',
                    'doc': 'application/msword',
                    'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'ppt': 'application/vnd.ms-powerpoint',
                    'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
                    'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                    'jpg': 'image/jpeg',
                    'jpeg': 'image/jpeg',
                    'png': 'image/png',
                    'gif': 'image/gif',
                    'zip': 'application/zip',
                    'hwp': 'application/x-hwp'
                };
                contentType = mimeTypes[ext] || 'application/octet-stream';
            }

            // Blob 생성
            const blob = new Blob([response.data], { type: contentType });
            
            // 다운로드 처리
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // 원본 파일명 사용
            link.setAttribute('download', doc.original_name || doc.name || doc.file_name);
            link.style.display = 'none';
            document.body.appendChild(link);
            link.click();
            
            // 정리
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } else {
            // 기존 파일인 경우 <a> 태그를 통한 다운로드
            const url = `${default_url}/api/general/manager_resume/getAttachmentDownload?file_name=${doc.file_name}`;
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.original_name || doc.name || doc.file_name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    } catch (error) {
        console.error('파일 다운로드 중 오류:', error);
        Swal.fire({
            icon: 'error',
            title: '다운로드 실패',
            text: error.message || '파일 다운로드 중 오류가 발생했습니다.'
        });
    }
};

const handleProfileImageChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    // 파일 크기 체크 (10MB)
    if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
            icon: 'error',
            title: '파일 크기 초과',
            text: '프로필 이미지는 10MB 이하여야 합니다.'
        })
        return
    }

    // 이미지 파일 체크
    if (!file.type.startsWith('image/')) {
        Swal.fire({
            icon: 'error',
            title: '파일 형식 오류',
            text: '이미지 파일만 업로드 가능합니다.'
        })
        return
    }

    try {
        // 썸네일 생성
        const thumbnailFile = await createThumbnail(file)
        
        // 미리보기 생성
        const reader = new FileReader()
        reader.onload = (e) => {
            resume.value.photo = e.target.result
        }
        reader.readAsDataURL(file)

        // 원본과 썸네일 모두 저장 (S3에 업로드용)
        resume.value.profileImageFile = file
        resume.value.profileThumbnailFile = thumbnailFile
    } catch (error) {
        console.error('이미지 처리 중 오류:', error)
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: '이미지 처리 중 오류가 발생했습니다.'
        })
    }
}

// 썸네일 생성 함수
const createThumbnail = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = (event) => {
            const img = new Image()
            img.src = event.target.result
            img.onload = () => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                
                // 썸네일 크기 설정
                const maxSize = 200
                let width = img.width
                let height = img.height
                
                if (width > height && width > maxSize) {
                    height = Math.round((height * maxSize) / width)
                    width = maxSize
                } else if (height > maxSize) {
                    width = Math.round((width * maxSize) / height)
                    height = maxSize
                }
                
                canvas.width = width
                canvas.height = height
                
                ctx.drawImage(img, 0, 0, width, height)
                
                // 압축된 썸네일을 Blob으로 변환
                canvas.toBlob(
                    (blob) => {
                        const thumbnailFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        })
                        resolve(thumbnailFile)
                    },
                    'image/jpeg',
                    0.5
                )
            }
            img.onerror = reject
        }
        reader.onerror = reject
    })
}

// 수정 모드 시작 함수 추가
const startEditMode = () => {
    localEditMode.value = true
    emit('update:isEditMode', true)
}

// emit 정의 추가
const emit = defineEmits(['close', 'update:resumeData', 'update:isEditMode'])
</script>

<style scoped>
.fixed {
    position: fixed;
}

/* 입력 필드 포커스 스타일 */
input[type="text"],
input[type="tel"],
input[type="email"],
textarea,
select {
    @apply transition-all duration-200 focus:outline-none focus:ring-1 border-gray-200;
}

input[type="text"]:focus,
input[type="tel"]:focus,
input[type="email"]:focus,
textarea:focus,
select:focus {
    @apply ring-blue-500 border-blue-500;
}

/* 에러 상태 스타일 */
input[class*="!border-red-500"] {
    border-color: rgb(239 68 68) !important;
}

input[class*="!border-red-500"]:focus {
    border-color: rgb(239 68 68) !important;
    --tw-ring-color: rgb(239 68 68) !important;
}

/* 기본 상태 스타일 */
.border-gray-200 {
    border-color: rgb(229 231 235);
}

.border-gray-200:focus {
    border-color: rgb(59 130 246);
    --tw-ring-color: rgb(59 130 246);
}
</style>
